// Translation strings for Swedish (sv).
//
// Split out of the former single translations.js so the three languages can be
// edited without colliding. t() has NO fallback - a key missing here renders as
// the raw key name, so every language must define every key.
// `npm run check:translations` asserts that.

const sv = {
  // Common
  cancel: 'Avbryt',
  save: 'Spara',
  delete: 'Ta bort',
  edit: 'Redigera',
  add: 'Lägg till',
  close: 'Stäng',
  search: 'Sök',
  loading: 'Laddar...',
  error: 'Fel',
  success: 'Lyckades',

  // Navigation
  login: 'Logga in',
  logout: 'Logga ut',
  adminPanel: 'Adminpanel',
  publicView: 'Offentlig vy',
  settings: 'Inställningar',
  account: 'Konto',
  menu: 'Meny',

  // User Management
  username: 'Användarnamn',
  password: 'Lösenord',
  email: 'E-post',
  role: 'Roll',
  superAdmin: 'Huvudadministratör',
  countryAdmin: 'Landsadministratör',
  editor: 'Redaktör',
  manageUsers: 'Hantera användare',
  changePassword: 'Byt lösenord',
  newPassword: 'Nytt lösenord',
  confirmPassword: 'Bekräfta lösenord',
  currentPassword: 'Nuvarande lösenord',
  allFieldsRequired: 'Alla fält är obligatoriska',
  passwordsDoNotMatch: 'Nya lösenord matchar inte',
  passwordTooShort: 'Nytt lösenord måste vara minst 6 tecken',
  passwordChangedSuccessfully: 'Lösenord ändrat framgångsrikt!',
  failedToChangePassword: 'Misslyckades att ändra lösenord',
  connectionError: 'Anslutningsfel',
  saveFailed: 'Sparande misslyckades',
  userManagement: 'Användarhantering',
  backToAdmin: 'Tillbaka till Admin',
  addNewUser: 'Lägg till ny användare',
  emailOptional: 'E-post (valfritt)',
  rolePermissions: 'Rollbehörigheter:',
  editorRole: 'Redaktör:',
  editorDescription: 'Serier, tävlingar, förare och resultat i sitt eget land (ingen åtkomst till användare)',
  superAdminRole: 'Huvudadministratör:',
  superAdminDescription: 'Alla länder, och den enda roll som kan utse en ny huvudadministratör',
  countryAdminRole: 'Landsadministratör:',
  countryAdminDescription: 'Allt en huvudadministratör kan göra, men bara i sitt eget land',
  countryAdminScopeHint: 'Du administrerar ett land. Serier, evenemang och konton i andra länder tillhör deras egna administratörer.',
  managedBySuperAdmin: 'Hanteras av en huvudadministratör',
  addUser: 'Lägg till användare',
  existingUsers: 'Befintliga användare',
  created: 'Skapad:',
  lastLogin: 'Senaste inloggning:',
  usernameAndPasswordRequired: 'Användarnamn och lösenord krävs',
  userCreatedSuccessfully: 'Användare skapad framgångsrikt',
  failedToCreateUser: 'Misslyckades skapa användare',
  deleteThisUser: 'Ta bort denna användare?',
  failedToDeleteUser: 'Misslyckades ta bort användare',
  editUser: 'Redigera användare',
  usernameRequired: 'Användarnamn krävs',
  userUpdatedSuccessfully: 'Användare uppdaterad framgångsrikt',
  failedToUpdateUser: 'Misslyckades uppdatera användare',
  resetPassword: 'Återställ lösenord',
  resetPasswordFor: 'Återställ lösenord för',
  minimumSixCharacters: 'Minst 6 tecken',
  passwordResetSuccessfully: 'Lösenord återställt framgångsrikt',
  failedToResetPassword: 'Misslyckades återställa lösenord',

  // Series Management
  series: 'Serie',
  seriesSettings: 'Serieinställningar',
  selectSeries: 'Välj serie',
  selectYear: 'Välj år',
  noSeriesForYear: 'Inga serier för år',
  createSeries: 'Skapa ny serie',
  seriesName: 'Serienamn',
  startYear: 'Startår',
  endYear: 'Slutår',
  yearHint: 'Slutår är valfritt. Använd det för vinterserier som sträcker sig över två år (t.ex. 2024-2025)',
  racingSeries: 'Tävlingsserier',
  newSeries: 'Ny serie',
  noSeriesYet: 'Inga serier än. Skapa din första!',
  deleteSelectedSeries: 'Ta bort vald serie',
  deleteThisSeries: 'Ta bort denna serie och all dess data?',
  editSeries: 'Redigera serie',
  seriesNameRequired: 'Serienamn krävs',
  seriesClass: 'Klass',
  seriesClasses: 'Klasser',
  seriesClassHelp: 'En serie kan köra flera klasser. Varje klass har egna förare, tävlingar och ställning.',
  addSeriesClass: 'Lägg till klass',
  seriesClassName: 'Klassens namn',
  seriesClassNameRequired: 'Klassens namn krävs',
  deleteSeriesClassConfirm: 'Ta bort klassen med dess tävlingar och resultat?',
  seriesClassLastCannotDelete: 'En serie måste ha minst en klass.',
  seriesClassHasResults: 'Klassen har registrerade resultat. Ta bort dem först.',
  seriesClassNameTaken: 'En klass med det namnet finns redan i serien',
  classSettings: 'Klassens inställningar',
  inheritFromSeries: 'Följ serien',
  inheritedValue: 'Seriens inställning',
  addExistingDriver: 'Lägg till en förare från serien',
  removeFromClass: 'Ta bort från klassen',
  removeFromClassConfirm: 'Ta bort föraren från klassen? Resultat i andra klasser påverkas inte.',
  deleteDriverEverywhere: 'Ta bort från hela serien',
  deleteDriverEverywhereConfirm: 'Ta bort föraren från seriens alla klasser med alla resultat?',
  carNumberTakenInClass: 'Startnumret tillhör redan någon annan i den här klassen',
  driverNotInClass: 'Föraren kör inte i den här serien',
  noDriversInThisClass: 'Inga förare i klassen',
  country: 'Land',
  // Grupperna i landväljaren. Slås upp dynamiskt som continent + gruppnamnet,
  // så check-translations.js ser dem inte - countryGroups.test.js kontrollerar
  // att alla åtta finns på alla tre språken.
  continentNordic: 'Norden och Baltikum',
  continentEurope: 'Europa',
  continentAfrica: 'Afrika',
  continentAsia: 'Asien',
  continentNorthAmerica: 'Nordamerika',
  continentSouthAmerica: 'Sydamerika',
  continentOceania: 'Oceanien',
  continentAntarctica: 'Antarktis',
  countryRequired: 'Land krävs',
  create: 'Skapa',
  update: 'Uppdatera',
  totalRaces: 'Totalt antal tävlingar',
  racesCounted: 'Räknade tävlingar',
  qualificationMaster: 'TQ mästare',
  calculateQualificationPoints: 'Beräkna kvalpoäng',
  juniorChampionship: 'Juniormästerskap',
  seniorChampionship: 'Seniormästerskap',
  ageLimit: 'Åldersgräns',
  totalRacesDescription: 'Hur många tävlingar serien kommer att ha totalt',
  racesCountedDescription: 'Hur många bästa resultat som räknas till mästerskapet. 0 = alla tävlingar räknas.',
  racesInSeries: 'tävlingar i serien',
  best: 'bästa',
  countedLabel: 'räknas',
  qualificationPointsDescription: 'Topp 5 kvalificerare får 5, 4, 3, 2, 1 bonuspoäng',
  qualificationMasterDescription: '🏆 Separat mästerskap för endast kvalpoäng',
  enable: 'Aktivera',
  yearsAndUnder: 'år och under',
  yearsOld: 'år gammal',
  plusYearsOld: '+ år gammal',
  driversAgeAndUnder: 'Förare {age} år och under',
  driversPlusYearsOld: 'Förare {age}+ år',
  tip: 'Tips:',
  changesAutoSaved: 'Ändringar sparas automatiskt. Resultaten uppdateras omedelbart för att återspegla dina inställningar.',
  exportChampionshipDataToExcel: 'Exportera mästerskapsdata till Excel',
  pleaseSelectAtLeastOneSeries: 'Välj minst en serie att exportera',
  exportFailed: 'Export misslyckades',
  failedToExportChampionship: 'Misslyckades med att exportera mästerskapsdata. Försök igen.',
  sessionExpired: 'Sessionen har gått ut. Logga in igen.',
  noSeriesAvailable: 'Inga serier tillgängliga',

  // Driver Management
  drivers: 'Förare',
  driverName: 'Förarnamn',
  birthYear: 'Födelseår',
  club: 'Klubb',
  addDriver: 'Lägg till förare',
  editDriver: 'Redigera förare',
  newDriver: 'Ny förare',
  carNumberOptional: 'Bilnummer (valfritt)',
  clubOptional: 'Klubb (valfritt)',
  birthYearOptional: 'Födelseår (valfritt)',
  noDriversYet: 'Inga förare ännu.',
  deleteThisDriver: 'Ta bort denna förare?',
  failedToRemoveFromClass: 'Det gick inte att ta bort föraren från klassen',
  selectAll: 'Välj alla',
  deselectAll: 'Avmarkera alla',
  deleteSelected: 'Ta bort valda',
  deleteSelectedDrivers: 'Ta bort {count} valda förare?',
  copyDrivers: 'Kopiera förare',
  copyFromOtherSeries: 'Kopiera från annan serie',
  copyDriversFromOtherSeries: 'Kopiera förare från annan serie',
  selectSeriesToCopyFrom: 'Välj serie att kopiera förare från',
  noOtherSeriesAvailable: 'Inga andra serier tillgängliga',
  copyingFrom: 'Kopierar från',
  selectDriversToCopy: 'Välj förare att kopiera',
  noDriversInThisSeries: 'Inga förare i denna serie',
  copySelected: 'Kopiera valda',
  back: 'Tillbaka',
  driversCopiedSuccessfully: '{count} förare kopierade framgångsrikt',
  failedToCopyDrivers: 'Misslyckades kopiera förare',

  // Race Management
  races: 'Tävlingar',
  raceName: 'Tävlingsnamn',
  raceDate: 'Datum',
  raceSequenceNumber: 'Tävlingsetikett (t.ex. SM2, Round 1)',
  addRace: 'Lägg till tävling',
  editRace: 'Redigera tävling',
  raceResults: 'Tävlingsresultat',
  addResults: 'Lägg till resultat',
  newRace: 'Ny tävling',
  deleteThisRace: 'Ta bort denna tävling?',
  pleaseSelectSeriesFirst: 'Välj en serie först',
  errorCreatingRace: 'Fel vid skapande av tävling: ',
  editResults: 'Redigera resultat',
  importFromExcel: 'Importera från Excel',
  importFromMyRCM: 'Importera från MyRCM',
  // LiveRC-import. liverc*-felnycklarna skickas till t() vid namn från
  // parseLiveRCUrl i utils/liverc.js, så check-translations.js ser inte att de
  // används - liverc.test.js kontrollerar i stället att alla finns.
  importFromLiveRC: 'Importera från LiveRC',
  howToImportFromLiveRC: 'Så här importerar du från LiveRC:',
  livercStep1: 'Öppna tävlingen på banans egen LiveRC-sida, till exempel gandsraceway.liverc.com',
  livercStep2: 'Kopiera adressen till tävlingssidan och klistra in den nedan',
  livercStep3: 'Välj klassen för den här tävlingen, kontrollera förarmatchningen och importera',
  livercUrlLabel: 'LiveRC-tävlingens adress',
  livercUrlHint: 'Tävlingssidan eller dess Final Results-sida. Alla klasser i tävlingen läses därifrån.',
  livercFetching: 'Läser LiveRC...',
  livercPickClass: 'Klass',
  livercPickClassPlaceholder: 'Välj klass för den här tävlingen',
  livercNoQualifying: 'Tävlingen har inga kvalresultat, så kvalpoängen lämnas tomma.',
  livercCreateNewDriver: 'Skapa en ny förare',
  livercLabel: 'LiveRC:',
  livercEventLoaded: 'Hittade {count} klass(er) i tävlingen',
  livercImported: 'Importerade {count} resultat till {class}',
  livercFetchFailed: 'Kunde inte nå LiveRC',
  livercEventNotFound: 'Tävlingen har inga publicerade resultat',
  livercNoResultsTable: 'Sidan har ingen resultattabell',
  livercUrlRequired: 'Klistra in en LiveRC-adress först',
  livercNotALiveRCUrl: 'Adressen är ingen LiveRC-adress',
  livercNotAnEventUrl: 'Adressen är en LiveRC-sida men inte en tävlingssida. Använd tävlingens eller Final Results-sidans adress.',
  livercNoEventId: 'Adressen saknar tävlingens id',
  failedToCreateDriver: 'Misslyckades att skapa förare',
  resultsAdminOnly: 'Resultat (endast admin)',
  deleteRace: 'Ta bort tävling',
  resetRaceResults: 'Återställ tävlingsresultat',
  resetRaceResultsConfirm: 'Är du säker på att du vill återställa alla resultat för denna tävling?',
  raceResultsReset: 'Tävlingsresultat återställda framgångsrikt',
  failedToResetResults: 'Misslyckades återställa resultat',
  editResultsFor: 'Redigera resultat:',
  driver: 'Förare',
  qualPos: 'Kval Pos',
  qualPts: 'Kval Poäng',
  saveResults: 'Spara resultat',
  failedToSaveRankings: 'Misslyckades spara placeringar',

  // Standings
  standings: 'Resultat',
  championshipStandings: 'Mästerskapsresultat',
  qualificationStandings: 'Kvalresultat',
  juniorStandings: 'Juniormästerskap',
  seniorStandings: 'Seniormästerskap',
  position: 'Pos.',
  name: 'Namn',
  totalPoints: 'Totalt',
  racesParticipated: 'Tävlingar',
  bestResult: 'Bästa resultat',
  qualificationMasterStandings: 'TQ Mästare Resultat',
  allQualificationPoints: 'ALLA Kvalpoäng',
  pureQualificationChampionship: 'Rent kvalmästerskap:',
  allQualificationPointsCounted: 'Alla kvalpoäng räknas (inga bortfall)',
  juniorChampionshipStandings: 'Juniormästerskap Resultat',
  seniorChampionshipStandings: 'Seniormästerskap Resultat',
  ageAndUnder: 'år och under',
  agePlus: 'år+',
  juniorDriversOnly: 'Endast juniorer:',
  seniorDriversOnly: 'Endast seniorer:',
  onlyDriversAgedIncluded: 'Endast förare i åldern',
  andUnderIncluded: 'och under inkluderade',
  andOverIncluded: '+ inkluderade',
  noStandingsYet: 'Inga resultat än.',
  noQualificationDataYet: 'Inga kvaldata än.',
  noJuniorDriversYet: 'Inga juniorförare med resultat än.',
  noSeniorDriversYet: 'Inga seniorförare med resultat än.',
  noRacesYet: 'Inga tävlingar än.',
  noResultsYet: 'Inga resultat än',
  driversCount: 'förare',
  born: 'Född:',
  dropped: 'bortfall',
  raceShort: 'Tävl',
  qualShort: 'Kval',
  points: 'poäng',
  total: 'Totalt',

  // Export & Import
  exportExcel: 'Exportera Excel',
  exportChampionships: 'Exportera mästerskap till Excel',
  selectSeriesToExport: 'Välj serier att exportera:',
  selectAtLeastOne: 'Välj minst en serie',
  selected: 'vald',
  export: 'Exportera',
  exportLanguage: 'Exportspråk',
  preview: 'Förhandsvisning',
  importing: 'Importerar...',
  chooseExcelFile: 'Välj Excel-fil',
  excelFileFormat: 'Excel-filformat:',
  columnDriver: 'Kolumn: Driver eller Name (förarnamn)',
  columnPosition: 'Kolumn: Position eller Pos (tävlingsposition: 1-50)',
  columnQual: 'Kolumn: Qual eller Q (valfritt, kvalposition: 1-5)',
  driversNotInSeriesCreated: 'Förare som inte finns i din serie skapas automatiskt.',
  resultsReadyToImport: 'resultat redo att importera',
  newDriversWillBeCreated: 'ny(a) förare kommer att skapas:',
  rowsSkipped: 'rad(er) hoppades över:',
  andMore: 'och {count} fler',
  rememberToAddClubAndBirthYear: '💡 Efter import: Kom ihåg att lägga till klubb- och födelseårsinformation för nya förare',
  tipAddClubAndBirthYear: '💡 Tips: Du kan nu lägga till klubb- och födelseårsinformation för dessa förare med redigeringsknappen (pennikon).',
  noDataFoundInExcel: 'Ingen data hittades i Excel-filen',
  missingDriverNameOrPosition: 'Saknar förarnamn eller position',
  successfullyParsedResults: '✓ Lyckades tolka {count} resultat från Excel-fil',
  errorReadingExcel: 'Fel vid läsning av Excel-fil: ',
  importResults: 'Importera {count} resultat',
  importFailed: 'Import misslyckades: ',
  new: 'NY',
  newDrivers: 'nya förare',
  howToImportFromMyRCM: 'Hur man importerar från MyRCM:',
  myrcmStep1: 'Öppna dina tävlingsresultat på MyRCM',
  myrcmStep2: 'Kopiera händelse-URL (t.ex. https://www.myrcm.ch/en/report/98862/393406)',
  myrcmStep3: 'Klistra in den nedan - systemet hämtar automatiskt både finaler och kvalificering',
  myrcmStep4: 'Om tävlingen bara har kval, kryssa i "Importera kval som final"',
  myrcmStep5: 'Klicka "Hämta & Förhandsgranska"',
  myrcmStep6: 'Granska förarmatchningar - åtgärda eventuella felmatchningar',
  myrcmStep7: 'Klicka "Importera resultat"',
  checkMatch: 'KONTROLLERA',
  matchedByCarNumberOnlyHint: 'Matchad enbart på bilnummer, inte på namn - bilnummer är per klass, så kontrollera att föraren stämmer',
  browseMyrcmEvents: 'Bläddra bland tävlingar',
  pasteUrlInstead: 'Klistra in URL',
  myrcmBrowseStep1: 'Välj en tävling i listan (filtrera efter land eller sök på namn)',
  myrcmBrowseStep2: 'Välj klass',
  myrcmBrowseStep3: 'Klicka "Hämta & Förhandsgranska"',
  myrcmScopeArchive: 'Tidigare tävlingar',
  myrcmScopeLive: 'Pågår nu',
  myrcmFilterCountry: 'Filtrera efter land',
  myrcmAllCountries: 'Alla länder',
  myrcmSearchEventsPlaceholder: 'Sök tävling, arrangör eller plats...',
  myrcmSearch: 'Sök',
  myrcmResults: 'Resultat',
  myrcmPreviousPage: 'Föregående',
  myrcmNextPage: 'Nästa',
  myrcmLoadingEvents: 'Hämtar tävlingar...',
  myrcmNoEventsFound: 'Inga tävlingar hittades',
  myrcmFailedToLoadEvents: 'Kunde inte hämta tävlingslistan',
  myrcmNoReportsBadge: 'inga resultat',
  myrcmSelectClass: 'Välj klass',
  myrcmLoadingClasses: 'Hämtar klasser...',
  myrcmNoClassesForEvent: 'Den här tävlingen har inga resultatklasser',
  myrcmFailedToLoadClasses: 'Kunde inte hämta klasslistan',
  myrcmBackToEvents: 'Tillbaka till tävlingar',
  myrcmSelectedEvent: 'Vald',
  myrcmChangeSelection: 'Ändra',
  myrcmSiteChanged: 'MyRCM-sidans struktur har ändrats och kunde inte läsas',
  myrcmUsePasteInstead: 'Klistra in en URL istället',
  myrcmResultsUrlLabel: 'MyRCM resultat-URL',
  urlWillBeUsedForBothFinalAndQual: 'Denna URL används för både final- och kvalresultat',
  enterTheBaseUrlWithoutReportKey: 'Ange bas-URL (reportKey läggs till automatiskt)',
  finalResultsUrl: 'Finalresultat-URL',
  qualificationResultsUrl: 'Kvalresultat-URL',
  optional: '(Valfritt)',
  fetchingFromMyRCM: 'Hämtar från MyRCM...',
  fetchAndPreview: 'Hämta & Förhandsgranska resultat',
  hideDriverMapping: 'Dölj',
  showDriverMapping: 'Visa',
  driverMapping: 'Förarmatchning',
  matchDrivers: '🔗 Matcha förare',
  someDriversNotMatched: 'Vissa förare kunde inte matchas automatiskt. Välj befintliga förare eller skapa nya:',
  createNewDriver: '➕ Skapa ny förare (#{carNumber})',
  matchToExistingDriver: 'Matcha med befintlig förare',
  unmatched: 'OMATCHAD',
  matched: 'MATCHAD',
  myrcmLabel: 'MyRCM: ',
  pleaseEnterFinalResultsUrl: 'Ange en finalresultat-URL',
  failedToFetchMyRCM: 'Misslyckades hämta MyRCM-sida',
  couldNotFindResultsTable: 'Kunde inte hitta resultattabell på sidan',
  noDriversFoundInTable: 'Inga förare hittades i resultattabellen. Kontrollera webbläsarens konsol.',
  successfullyFetchedFromMyRCM: '✓ Lyckades hämta {count} resultat från MyRCM',
  driversNeedManualMatching: '⚠ {count} förare behöver manuell matchning eller kommer att skapas som nya',
  pos: 'Pos',
  qPos: 'K Pos',
  qPts: 'K Poäng',
  pts: 'Poäng',
  importQualificationAsFinal: 'Importera kvalificering som slutresultat',
  importQualificationAsFinalDescription: 'Använd detta alternativ när tävlingen bara har kvalificering utan final',

  // Table headers for export
  positionShort: 'Pos.',
  nameColumn: 'Namn',
  clubColumn: 'Klubb',
  totalColumn: 'Totalt',

  // Language selector
  language: 'Språk',
  finnish: 'Finska',
  english: 'Engelska',
  swedish: 'Svenska',

  // Help System
  help: 'Hjälp',
  helpPublicTitle: 'Välkommen till RC Racing Series',
  helpPublicDescription: 'Detta system visar RC-tävlingsmästerskap. Du kan bläddra mellan olika serier, år och länder.',
  helpViewingStandings: 'Visa resultat',
  helpSelectCountry: 'Välj ett land från menyn längst upp till höger',
  helpSelectYear: 'Välj ett år från rullgardinsmenyn',
  helpSelectSeries: 'Välj en serie från rullgardinsmenyn',
  helpViewTabs: 'Bläddra mellan flikar för att se mästerskapsresultat, kvalresultat och andra kategorier',
  helpStandingsTip: 'Resultat uppdateras automatiskt efter tävlingarnas slut. Bortfall visas med transparent typsnitt.',
  helpUnderstandingStandings: 'Förstå resultatkategorier',
  helpChampionshipExplanation: 'Huvudresultat från alla tävlingar, med eventuella bortfall',
  helpQualificationExplanation: 'Bonuspoäng från TOP 5 kvalresultat (5,4,3,2,1 poäng)',
  helpJuniorExplanation: 'Separat mästerskap för unga förare (definierad åldersgräns)',
  helpSeniorExplanation: 'Separat mästerskap för äldre förare (definierad åldersgräns)',
  helpRaceResults: 'Tävlingsresultat',
  helpRaceResultsDescription: 'Varje tävling visar positioner, poäng och kvalpositioner. Klicka på tävlingar för att se detaljerade resultat.',
  helpAdminTitle: 'Adminpanelguide',
  helpAdminDescription: 'Här kan du hantera serier och deras klasser, förare, tävlingar, resultat samt evenemang med onlineanmälan.',
  helpManagingSeries: 'Hantera serier',
  helpSeriesDescription: 'Serier är mästerskapssäsonger. Varje serie kan sträcka sig över ett eller flera år.',
  helpCreateSeries: 'Skapa en ny serie genom att ange namn, land, start- och slutår',
  helpConfigureSeries: 'Konfigurera serieinställningar (bortfall, kvalpoäng, etc.)',
  helpAddDrivers: 'Lägg till förare i en klass, manuellt eller kopierade från en annan klass',
  helpAddRaces: 'Lägg till tävlingar i en klass och importera resultat',
  helpSeriesSettingsTip: 'Alla inställningsändringar sparas automatiskt och resultaten uppdateras omedelbart.',
  helpManagingDrivers: 'Hantera förare',
  helpDriversDescription: 'Förare hör till en klass. Lägg till dem i en klass innan du matar in resultat.',
  helpAddDriverManually: 'Lägg till en förare genom att klicka på "Lägg till förare" och fylla i detaljerna',
  helpCopyDrivers: 'Kopiera förare från en annan serie för att spara tid',
  helpEditDriverInfo: 'Uppdatera förarinformation (namn, klubb, födelseår) med pennikonen',
  helpDriversTip: 'Födelseår behövs för junior- och seniormästerskap. Bilnummer är valfritt men rekommenderas.',
  helpManagingRaces: 'Hantera tävlingar',
  helpRacesDescription: 'En tävling hör till en klass och innehåller datum, namn och resultat.',
  helpCreateRace: 'Skapa en tävling genom att ange datum, namn och identifierare',
  helpAddResultsManually: 'Lägg till resultat manuellt eller importera dem',
  helpImportingResults: 'Importera resultat',
  helpExcelImport: 'Importera resultat från Excel-fil (obligatoriska kolumner: Driver/Name, Position/Pos, Qual/Q)',
  helpMyRCMImport: 'Importera resultat direkt från MyRCM-systemet med URL',
  helpRacesTip: 'Efter import, verifiera att alla förare matchas korrekt. Du kan åtgärda felaktiga matchningar innan import.',
  helpExcelFormat: 'Excel-filformat',
  helpExcelFormatDescription: 'Din Excel-fil bör ha minst dessa kolumner:',
  helpExportingData: 'Exportera data',
  helpExportDescription: 'Du kan exportera mästerskapsresultat till Excel-fil för delning.',
  helpOpenExportDialog: 'Klicka på "Exportera Excel" från menyn',
  helpSelectExportSeries: 'Välj serier att exportera (du kan välja flera)',
  helpChooseExportLanguage: 'Välj exportspråk',
  helpDownloadExcel: 'Klicka på "Exportera" för att ladda ner Excel-filen',
  helpSeriesSettingsDetail: 'Serieinställningsdetaljer',
  helpTotalRacesDetail: 'Ange hur många tävlingar serien kommer att ha totalt.',
  helpRacesCountedDetail: 'Ange hur många bästa resultat som räknas till mästerskapet. När fler tävlingar har körts än detta antal släpps de sämsta resultaten automatiskt.',
  helpQualificationPointsDetail: 'När aktiverat får TOP 5 kvalresultat bonuspoäng (5,4,3,2,1). Dessa poäng läggs till huvudresultaten.',
  helpQualificationMasterDetail: 'Skapa ett separat mästerskap från endast kvalpoäng (inga bortfall). Visar vem som är bäst i kvalet.',
  helpAgeChampionshipsDetail: 'Skapa separata mästerskap för åldersgrupper. Definiera åldersgräns (t.ex. 18 för juniorer, 40 för seniorer).',
  helpCommonFeatures: 'Gemensamma funktioner',
  helpLanguageDescription: 'Byt gränssnittsspråk (Finska, Engelska, Svenska)',
  helpCountryDescription: 'Filtrera serier efter land för att endast se det landets tävlingar',
  helpYearDescription: 'Välj år för att se den säsongens serier',
  helpNeedMoreHelp: 'Behöver du mer hjälp?',
  helpContactDescription: 'Om du har frågor eller problem, kontakta systemadministratören.',
  helpVersion: 'Version',
  helpSystemName: 'Systemnamn',
  // Help - classes, calendar, online entry, imports (added 2026-09)
  helpSelectClass: 'Välj en klass inom serien — varje klass har sin egen ställning',
  helpRaceCalendar: 'Tävlingskalender',
  helpRaceCalendarDescription: 'Kalendern listar kommande och tidigare evenemang. Öppna ett evenemang för att se dess detaljer, klasser och anmälda.',
  helpCalendarColours: 'Varje evenemang färgas efter anmälningsstatus: anmälan öppen, inte öppen än, eller stängd.',
  helpEntering: 'Anmäla sig till en tävling',
  helpEnteringDescription: 'När ett evenemang tar emot anmälningar på denna webbplats, öppna det och tryck på Anmäl.',
  helpEnterPickClasses: 'Välj klasserna du vill köra och ange ett transpondernummer för varje — du kan lämna det tomt och lösa det på banan.',
  helpEnterDetails: 'Fyll i ditt namn, klubb, bilnummer och kontaktuppgifter.',
  helpEnterExtras: 'Svara på arrangörens extra frågor — till exempel en lunchbeställning eller ett däckval. Vissa frågor visas först när du svarat på en relaterad fråga.',
  helpEnterSaveLink: 'Spara länken du får. Den skickas inte via e-post och är enda sättet att ändra eller dra tillbaka din anmälan senare.',
  helpEnteringTip: 'Om en klass är full hamnar du på väntelistan och arrangören hör av sig.',
  helpClasses: 'Serier och klasser',
  helpClassesDescription: 'En serie är mästerskapssäsongen. Dess klasser (till exempel 1:8 Nitro, 1:10 el) är där förare, tävlingar och resultat faktiskt finns — skapa alltså först en klass under en serie och lägg sedan till förare och tävlingar i den klassen.',
  helpEvents: 'Evenemang och onlineanmälan',
  helpEventsDescription: 'Ett evenemang är en tävling som man kan anmäla sig till online. Arrangörer kan skapa och redigera sina egna evenemang.',
  helpEventCreate: 'Skapa ett evenemang med namn, plats, datum och klasser.',
  helpEventEntryWindow: 'Ställ in anmälningsperioden — anmälan öppnar och stänger de datum du väljer, och kalendern visar statusen.',
  helpEventQuestions: 'Lägg till extra frågor för anmälda (lunch, däckval och så vidare). En fråga kan ställas in att visas endast när en annan besvarats med ja.',
  helpEventExternalEntry: 'Om anmälan sköts av ett annat system, lägg till dess adress så skickar anmälningsknappen användarna dit istället.',
  helpEventEntrantList: 'Ladda ner anmälningslistan, inklusive svaren på dina extra frågor och deras totalsummor.',
  helpAddAllClasses: 'Använd "Lägg till alla från serie" för att lägga till mästerskapets alla klasser till ett evenemang med en knapptryckning.',
  helpEventCopy: 'Kopiera ett evenemang för att återanvända dess klasser och frågor — allt kopieras utom datumen.',
  helpCopyRace: 'Kopiera en tävling till nästa år för att föra över dess inställningar till den nya säsongen, och ange sedan de nya datumen.',
  helpLiveRCImport: 'Importera resultat direkt från LiveRC med evenemangets adress — varje klass i evenemanget läses från den.',
  // Events / calendar
  eventManagement: 'Tävlingshantering',
  eventsTab: 'Kalender',
  addEvent: 'Lägg till tävling',
  editEvent: 'Redigera tävling',
  deleteEvent: 'Ta bort tävling',
  eventName: 'Tävlingens namn',
  eventOrganizer: 'Arrangör',
  eventVenue: 'Bana',
  eventAddress: 'Adress',
  eventCity: 'Ort',
  eventCountry: 'Land',
  eventChampionship: 'Mästerskap',
  championshipNone: 'Vanlig tävling',
  championshipEuropean: 'EM-tävling',
  championshipWorld: 'VM-tävling',
  championshipHint: 'Ett mästerskap visas i alla länders kalendrar, inte bara i sitt eget.',
  championshipHostedIn: 'Arrangörsland',
  championshipReadOnly: 'Arrangeras av {country}',
  eventStartDate: 'Startdatum',
  eventEndDate: 'Slutdatum',
  eventEndDateOptional: 'Slutdatum (valfritt)',
  eventDescription: 'Beskrivning',
  eventStatus: 'Status',
  eventStatusDraft: 'Utkast',
  eventStatusPublished: 'Publicerad',
  eventStatusCancelled: 'Inställd',
  eventEntryOpens: 'Anmälan öppnar',
  eventEntryCloses: 'Anmälan stänger',
  eventEntryInstructions: 'Anmälningsinstruktioner',
  eventEntryListPublic: 'Visa anmälda offentligt',
  eventEntryListPublicDescription: 'Den publika vyn visar endast namn, klubb, startnummer och klasser.',
  eventSupportedTransponders: 'Transpondrar som stöds',
  eventContactEmail: 'Kontakt-e-post',
  eventContactPhone: 'Kontakttelefon',
  eventWebsite: 'Webbplats',
  eventOwner: 'Ägare',
  eventClasses: 'Klasser',
  eventAddClass: 'Lägg till klass',
  eventClassName: 'Klassens namn',
  eventClassSeries: 'Serie',
  eventClassNoSeries: 'Ingen serie',
  eventClassEntryLimit: 'Deltagargräns',
  eventClassLinkedResults: 'Resultat länkade',
  eventClassRoundReady: 'Deltävling klar',
  eventClassRoundReadyHelp: 'Klassen har sin deltävling i serien. Resultaten kan importeras dit när loppet är kört.',
  eventNoClasses: 'Inga klasser ännu',
  // Tillägg - frågorna en arrangör lägger till i sin egen anmälan.
  // Arrangörens egen frågetext sparas som den skrivits och översätts aldrig;
  // bara gränssnittet runt den är översatt.
  eventExtras: 'Tillägg och förhandsbeställningar',
  eventExtrasHelp: 'Vad mer behöver du fråga en deltagare: lunch, däcksatser, depåplats. Endast antal, inga priser.',
  eventNoExtras: 'Inga extra frågor',
  eventAddExtra: 'Lägg till fråga',
  extraLabel: 'Vad vill du fråga?',
  extraHelpText: 'Hjälptext (valfritt)',
  extraType: 'Svar',
  extraTypeYesNo: 'Ja / nej',
  extraTypeNumber: 'Antal',
  extraTypeText: 'Fri text',
  extraRequired: 'Måste besvaras',
  extraAlwaysAsked: 'Frågas alltid',
  extraOnlyWhen: 'endast när: {question}',
  extraDependencyLocked: 'En annan fråga beror på den här, så den måste förbli ja/nej och kan inte själv vara villkorlig.',
  extraMinValue: 'Minst',
  extraMaxValue: 'Högst',
  extraLabelRequired: 'Skriv frågan först',
  extraLabelTooLong: 'Frågan är för lång',
  extraTypeLocked: 'Det finns redan svar, så svarstypen kan inte ändras. Lägg till en ny fråga i stället.',
  extraAnswered: '{count} har svarat',
  deleteExtraConfirm: 'Radera den här frågan?',
  deleteExtraDiscardConfirm: '{count} har svarat på den här. Radera ändå och kasta bort svaren?',
  failedToSaveExtra: 'Kunde inte spara frågan',
  failedToDeleteExtra: 'Kunde inte radera frågan',
  entryExtras: 'Tillägg',
  entryExtrasHelp: 'Arrangören vill veta:',
  entrantExtrasTotals: 'Beställt totalt',
  // Lägga till en hel series klasser på en gång.
  eventAddAllFromSeries: 'Lägg till alla från serien',
  eventAddAllFromSeriesHelp: 'Lägger till den valda seriens alla klasser i tävlingen och hoppar över dem som redan finns.',
  eventPickSeriesFirst: 'Välj en serie först',
  eventAddAllFromSeriesDone: '{created} klasser tillagda',
  eventAddAllFromSeriesSkipped: '{skipped} fanns redan',
  eventAddAllFromSeriesNone: 'Tävlingen har redan seriens alla klasser',
  // Kopiera en tävling.
  eventCopy: 'Kopiera',
  eventCopyTitle: 'Kopiera tävling',
  eventCopySuffix: '{name} (kopia)',
  eventCopyDatesCleared: 'Allt kopieras utom datumen. Fyll i tävlingsdatum och anmälningstid för den nya tävlingen.',
  eventCopyCarries: '{classes} klasser och {extras} tillägg kopieras. Du kan redigera dem när den sparats.',
  noEvents: 'Inga tävlingar',
  eventNameRequired: 'Tävlingens namn krävs',
  eventStartDateRequired: 'Startdatum krävs',
  eventClassNameRequired: 'Klassens namn krävs',
  deleteEventConfirm: 'Ta bort den här tävlingen?',
  deleteClassConfirm: 'Ta bort den här klassen?',
  failedToLoadEvents: 'Kunde inte hämta tävlingar',
  failedToSaveEvent: 'Kunde inte spara tävlingen',
  failedToDeleteEvent: 'Kunde inte ta bort tävlingen',
  failedToSaveClass: 'Kunde inte spara klassen',
  failedToDeleteClass: 'Kunde inte ta bort klassen',
  eventMineOnly: 'Endast mina',
  eventAllYears: 'Alla år',
  eventBasicInfo: 'Grundinformation',
  eventLocation: 'Plats',
  eventEntrySettings: 'Anmälan',
  eventExternalEntry: 'Anmälan sköts av ett annat system',
  eventExternalEntryHint: 'Anmälningsknappen skickar deltagaren dit i stället. Den här webbplatsen samlar inte in anmälningar för tävlingen.',
  eventExternalEntryUrl: 'Anmälningsadress',
  entryRegisterExternally: 'Anmäl dig på',
  entryHandledElsewhere: 'Anmälan till den här tävlingen sköts utanför den här webbplatsen.',
  externalEntryNeedsUrl: 'Ange adressen deltagarna ska skickas till.',
  // Roles
  organizer: 'Arrangör',
  competitor: 'Deltagare',
  organizerRole: 'Arrangör:',
  organizerDescription: 'Kan skapa och redigera sina egna tävlingar och ladda ner deras anmälningslistor. Ingen åtkomst till serier eller resultat.',
  competitorRole: 'Deltagare:',
  competitorDescription: 'Kan se och redigera sina egna uppgifter och sina egna anmälningar.',
  // Public calendar
  calendar: 'Tävlingskalender',
  calendarTitle: 'Tävlingskalender',
  calendarBack: 'Tillbaka till kalendern',
  calendarNoEvents: 'Inga tävlingar matchar filtren',
  calendarAllCountries: 'Alla länder',
  calendarLoading: 'Hämtar tävlingar...',
  calendarFailedToLoad: 'Kunde inte hämta tävlingskalendern',
  calendarLegacyRace: 'Deltävling',
  calendarEntryOpen: 'Anmälan öppen',
  calendarEntryNotYetOpen: 'Anmälan har inte öppnat',
  calendarEntryClosed: 'Anmälan stängd',
  calendarEntrants: 'anmälda',
  calendarEventNotFound: 'Tävlingen hittades inte',
  eventDetails: 'Tävlingsinformation',
  eventEntrantList: 'Anmälda',
  eventNoEntrants: 'Inga anmälda ännu',
  eventEntrantListHidden: 'Arrangören publicerar inte anmälningslistan.',
  eventCarNumber: 'Startnummer',
  eventClub: 'Klubb',
  eventEntryLimitReached: 'Fullt',
  eventCancelled: 'Den här tävlingen är inställd.',
  eventNoTransponders: 'Ej angivet',
  eventContact: 'Kontakt',
  calendarMonths: 'jan,feb,mar,apr,maj,jun,jul,aug,sep,okt,nov,dec',
  // Month grid
  calendarViewGrid: 'Månad',
  calendarViewList: 'Lista',
  calendarPrevMonth: 'Föregående månad',
  calendarNextMonth: 'Nästa månad',
  calendarToday: 'Idag',
  calendarWeekdays: 'mån,tis,ons,tors,fre,lör,sön',
  calendarMonthsFull: 'januari,februari,mars,april,maj,juni,juli,augusti,september,oktober,november,december',
  calendarMoreEvents: '+{count} till',
  calendarNoEventsThisMonth: 'Inga tävlingar denna månad',
  eventClassSeriesLocked: 'Serien kan inte ändras eftersom resultat redan är länkade till klassen.',
  eventClassSaved: 'Klassen uppdaterad',

  // Entries
  entryEnter: 'Anmäl dig',
  entryTitle: 'Anmälan',
  entryFirstName: 'Förnamn',
  entryLastName: 'Efternamn',
  entryTransponderNumber: 'Transpondernummer',
  entryClassTransponder: 'Transponder',
  entryTransponderPerClassHint: 'Ange transpondernumret du använder i varje klass. Du kan lämna det tomt och fylla i det på plats.',
  transponderNumberPlaceholder: 't.ex. 0012345',
  entryCarNumber: 'Startnummer',
  entryClub: 'Klubb',
  entryBirthYear: 'Födelseår',
  entryEmail: 'E-post',
  entryPhone: 'Telefon',
  entryNotes: 'Övrigt',
  entrySelectClasses: 'Välj klasser',
  entryOptional: 'valfritt',
  entrySubmit: 'Skicka anmälan',
  entrySubmitting: 'Skickar...',
  entrySuccessTitle: 'Anmälan mottagen',
  entrySuccessWaitlisted: 'Klassen är full, så du står på väntelistan. Arrangören hör av sig.',
  entryManageLinkTitle: 'Spara den här länken',
  entryManageLinkHelp: 'Med länken kan du ändra eller återta din anmälan. Den skickas inte per e-post, så spara den nu.',
  entryCopyLink: 'Kopiera länk',
  entryLinkCopied: 'Kopierad',
  entryMyEntriesOnDevice: 'Anmälningar gjorda på den här enheten',
  entryManageTitle: 'Din anmälan',
  entryWithdraw: 'Återta anmälan',
  entryWithdrawConfirm: 'Återta den här anmälan?',
  entryWithdrawn: 'Din anmälan har återtagits.',
  entryUpdated: 'Dina uppgifter sparades.',
  entryNotFound: 'Anmälan hittades inte. Kontrollera länken.',
  entryClosedForChanges: 'Anmälan har stängt. Kontakta arrangören.',
  entryHumanCheck: 'Bekräfta att du är människa',
  entryTurnstileMissing: 'Botskyddet är inte konfigurerat, så anmälan är inte tillgänglig.',
  entryDuplicateTransponder: 'Transpondernumret används redan i den här tävlingen. Kontrollera numret.',
  entryPickAtLeastOneClass: 'Välj minst en klass',
  entryFailed: 'Anmälan misslyckades',
  entryStatusRegistered: 'Anmäld',
  entryStatusWaitlisted: 'Väntelista',
  entryStatusCancelled: 'Återtagen',

  // Signup during entry
  entryCreateAccount: 'Skapa ett konto samtidigt',
  entryCreateAccountHelp: 'Med ett konto kan du hantera dina anmälningar senare utan en separat länk, och dina uppgifter fylls i nästa gång.',
  entryPassword: 'Lösenord',
  entryPasswordHint: 'Minst 8 tecken',
  entryAccountCreated: 'Kontot har skapats - du är nu inloggad.',
  entryPasswordTooShort: 'Lösenordet måste vara minst 8 tecken',
  entrySignedInAs: 'Inloggad som',

  // Entrant list
  entrantList: 'Anmälda',
  entrantListOpen: 'Visa anmälda',
  entrantCount: 'anmälda',
  entrantNone: 'Inga anmälda ännu',
  entrantExport: 'Ladda ner för tidtagning',
  entrantExportFormat: 'Tidtagningssystem',
  entrantExportExcel: 'Ladda ner Excel',
  entrantExportCsv: 'Ladda ner CSV',
  entrantFilterAll: 'Alla',
  entrantTransponder: 'Transponder',
  entrantContact: 'Kontakt',
  entrantStatus: 'Status',
  entrantFailedToLoad: 'Kunde inte hämta anmälda',
  entrantExportFailed: 'Nedladdningen misslyckades',
  entrantNoTransponder: 'ingen transponder',
  entrantRemove: 'Ta bort anmälan',
  entrantRemoveConfirm: 'Ta bort den här anmälan permanent?',

  // Competitor account
  myTransponders: 'Mina transpondrar',
  myTranspondersHint: 'Listan fylls på av sig själv när du anmäler dig: ett nytt nummer sparas här och erbjuds nästa gång.',
  transponderAdd: 'Lägg till',
  transponderLabelField: 'Namn (valfritt)',
  transponderRemove: 'Ta bort från listan',
  transponderNoneSaved: 'Du har inte sparat några transpondrar än.',
  accountTitle: 'Mina uppgifter',
  accountProfile: 'Grunduppgifter',
  accountMyEntries: 'Mina anmälningar',
  accountNoEntries: 'Du har inte anmält dig till någon tävling ännu',
  accountSaved: 'Dina uppgifter sparades',
  accountSaveFailed: 'Kunde inte spara dina uppgifter',
  accountLoadFailed: 'Kunde inte hämta dina uppgifter',
  accountPrefillHelp: 'De här uppgifterna fylls i åt dig när du anmäler dig till en tävling.',
  accountBrowseCalendar: 'Bläddra i tävlingskalendern',
  accountEntryClosed: 'Anmälan stängd',
  accountManageEntry: 'Redigera',
  accountUsername: 'Användarnamn',

  // Linking results to a series
  linkResults: 'Aktivera resultat',
  linkResultsHelp: 'Skapar en deltävling i serien för klassens resultat. Gör det när du är redo att registrera resultat.',
  linkResultsDone: 'Resultaten är länkade till serien',
  linkResultsNoSeries: 'Koppla klassen till en serie först',
  syncDrivers: 'Gör anmälda till förare',
  syncDriversHelp: 'Skapar förare i serien utifrån de anmälda, så att resultat kan registreras på dem.',
  syncDriversResult: '{created} skapade, {matched} matchade, {already} redan länkade',
  syncDriversConflicts: 'Konflikter',
  syncDriversConflictNumber: 'startnumret tillhör redan en annan förare i den här serien',
  linkFailed: 'Det gick inte',
  entryLoginIsEmail: 'Inloggning (e-postadress)',
  entryLoginIsEmailHint: 'Du loggar in med den här e-postadressen. Inget separat användarnamn behövs.',
  entryEmailRequiredForAccount: 'En e-postadress krävs för att skapa ett konto',
  entryEmailTaken: 'Det finns redan ett konto för den e-postadressen',

  // Password reset
  forgotPassword: 'Glömt lösenordet?',
  forgotPasswordTitle: 'Nytt lösenord',
  forgotPasswordHelp: 'Ange din e-postadress så skickar vi en länk för att sätta ett nytt lösenord.',
  forgotPasswordSend: 'Skicka länken',
  forgotPasswordSent: 'Om adressen har ett konto är en länk på väg. Kontrollera även skräpposten.',
  forgotPasswordCompetitorsOnly: 'Detta gäller deltagarkonton. Personalens lösenord återställs av en administratör.',
  resetPasswordTitle: 'Sätt ett nytt lösenord',
  resetPasswordNew: 'Nytt lösenord',
  resetPasswordConfirm: 'Bekräfta lösenordet',
  resetPasswordSubmit: 'Spara lösenordet',
  resetPasswordDone: 'Ditt lösenord har ändrats. Du kan logga in nu.',
  resetPasswordExpired: 'Länken är inte längre giltig. Begär en ny.',
  resetPasswordMismatch: 'Lösenorden matchar inte',
  resetPasswordChecking: 'Kontrollerar länken...',
  backToLogin: 'Till inloggningen',
};

export default sv;
